import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Clock, 
  Coffee, 
  LogIn, 
  LogOut, 
  Timer,
  TrendingUp,
  Calendar,
  Loader2,
  MapPin,
  Building2,
  AlertTriangle,
  Wifi,
  WifiOff,
  Truck,
  ShieldAlert
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { InstallHint } from "../components/InstallHint";
import { TIME_CHANGED_EVENT } from "../hooks/useSiteReminders";
import { announceClockOut } from "../components/ClockOutConfirm";
import { fmtTz } from "../lib/time";

export default function EmployeeDashboard() {
  const { authAxios, user } = useAuth();
  const [todayEntry, setTodayEntry] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Location tracking state
  const [locationStatus, setLocationStatus] = useState('checking'); // checking, active, denied, lost
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const isDriver = !!user?.is_truck_driver;
  const watchIdRef = useRef(null);
  const lastUpdateRef = useRef(Date.now());

  // Start location tracking
  const startLocationTracking = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationStatus('denied');
      return;
    }

    // Clear any existing watch
    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        const { latitude, longitude, accuracy, speed, heading } = position.coords;
        setCurrentLocation({ latitude, longitude });
        setLocationAccuracy(accuracy);
        setLocationStatus('active');

        // Send location update to backend every 30 seconds or on significant movement
        const now = Date.now();
        if (now - lastUpdateRef.current >= 30000) {
          lastUpdateRef.current = now;
          try {
            const status = todayEntry?.status === 'working' ? 'clocked_in' : 
                          todayEntry?.status === 'on_break' ? 'clocked_in' : 'online';
            await authAxios.post('/location/update', {
              latitude,
              longitude,
              accuracy_meters: accuracy,
              speed_kmh: speed ? speed * 3.6 : null, // Convert m/s to km/h
              heading,
              status
            });
          } catch (error) {
            console.error('Failed to update location:', error);
          }
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setLocationStatus('denied');
        } else {
          setLocationStatus('lost');
        }
      },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 10000 }
    );
  }, [authAxios, todayEntry?.status]);

  // Stop location tracking on unmount
  useEffect(() => {
    startLocationTracking();
    
    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [startLocationTracking]);

  const fetchProjects = useCallback(async () => {
    try {
      const res = await authAxios.get('/projects?active_only=true');
      setProjects(res.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [authAxios]);

  const fetchTodayEntry = useCallback(async () => {
    try {
      const res = await authAxios.get('/time/today');
      setTodayEntry(res.data);
      if (res.data.project_id) {
        setSelectedProject(res.data.project_id);
      }
    } catch (error) {
      console.error('Failed to fetch today entry:', error);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    fetchProjects();
    fetchTodayEntry();
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, [fetchProjects, fetchTodayEntry]);

  useEffect(() => {
    window.addEventListener(TIME_CHANGED_EVENT, fetchTodayEntry);
    return () => window.removeEventListener(TIME_CHANGED_EVENT, fetchTodayEntry);
  }, [fetchTodayEntry]);

  useEffect(() => {
    if (todayEntry?.status === 'working' || todayEntry?.status === 'on_break') {
      const timer = setInterval(fetchTodayEntry, 60000);
      return () => clearInterval(timer);
    }
  }, [todayEntry?.status, fetchTodayEntry]);

  const handleClockIn = async () => {
    if (!selectedProject && !isDriver) {
      toast.error('Please select a project/site before clocking in');
      return;
    }
    if (locationStatus === 'denied') {
      toast.error('Location access is required to clock in. Please enable location in your settings.');
      return;
    }
    if (!currentLocation) {
      toast.error('Unable to get your location. Please wait and try again.');
      return;
    }

    setActionLoading(true);
    try {
      const res = await authAxios.post('/time/clock-in', {
        project_id: selectedProject && selectedProject !== 'none' ? selectedProject : null,
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude
      });
      setTodayEntry(res.data);
      await authAxios.post('/location/update', {
        latitude: currentLocation.latitude,
        longitude: currentLocation.longitude,
        accuracy_meters: locationAccuracy || 10,
        status: 'clocked_in'
      }).catch(() => {});
      window.dispatchEvent(new Event(TIME_CHANGED_EVENT));
      toast.success(`Clocked in at ${res.data.project_name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      const res = await authAxios.post('/time/clock-out', currentLocation
        ? { latitude: currentLocation.latitude, longitude: currentLocation.longitude }
        : {});
      setTodayEntry(res.data);
      if (currentLocation) {
        await authAxios.post('/location/update', {
          latitude: currentLocation.latitude,
          longitude: currentLocation.longitude,
          accuracy_meters: locationAccuracy || 10,
          status: 'clocked_out'
        }).catch(() => {});
      }
      window.dispatchEvent(new Event(TIME_CHANGED_EVENT));
      announceClockOut(res.data);
      toast.success('Clocked out! Great work today!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBreakStart = async () => {
    setActionLoading(true);
    try {
      const res = await authAxios.post('/time/break-start');
      setTodayEntry(res.data);
      toast.success('Break started! Enjoy your rest ☕');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start break');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBreakEnd = async () => {
    setActionLoading(true);
    try {
      const res = await authAxios.post('/time/break-end');
      setTodayEntry(res.data);
      toast.success('Break ended! Back to work 💪');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end break');
    } finally {
      setActionLoading(false);
    }
  };

  const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    return fmtTz(isoString, 'HH:mm');
  };

  const getStatusBadge = () => {
    const status = todayEntry?.status || 'off';
    const styles = {
      working: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      break: 'bg-amber-100 text-amber-800 border-amber-200',
      off: 'bg-stone-100 text-stone-600 border-stone-200'
    };
    const labels = {
      working: 'Working',
      break: 'On Break',
      off: 'Off'
    };
    return (
      <Badge 
        variant="outline" 
        className={`${styles[status]} px-4 py-1.5 text-sm font-bold uppercase tracking-wider`}
        data-testid="status-badge"
      >
        {labels[status]}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const status = todayEntry?.status || 'off';
  const isWorking = status === 'working';
  const isOnBreak = status === 'on_break';
  const isClockedIn = isWorking || isOnBreak;
  const isClockedOut = !isClockedIn;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="employee-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-stone-900 font-heading">
              Good {+fmtTz(currentTime, 'H') < 12 ? 'Morning' : +fmtTz(currentTime, 'H') < 18 ? 'Afternoon' : 'Evening'}, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-stone-500 mt-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {fmtTz(currentTime, 'EEEE, MMMM d, yyyy')}
            </p>
          </div>
          {getStatusBadge()}
        </div>
      </div>

      <InstallHint />

      {/* Location Status Indicator */}
      <div className={`mb-6 p-3 rounded-xl flex items-center gap-3 ${
        locationStatus === 'active' ? 'bg-emerald-50 border border-emerald-200' :
        locationStatus === 'denied' ? 'bg-rose-50 border border-rose-200' :
        locationStatus === 'lost' ? 'bg-amber-50 border border-amber-200' :
        'bg-blue-50 border border-blue-200'
      }`}>
        {locationStatus === 'active' ? (
          <>
            <Wifi className="w-5 h-5 text-emerald-600" />
            <span className="text-emerald-700 text-sm">Location Active — Your location is being shared with LIL BEAR TRACKS</span>
            {locationAccuracy && (
              <span className="text-emerald-500 text-xs ml-auto">±{Math.round(locationAccuracy)}m accuracy</span>
            )}
          </>
        ) : locationStatus === 'denied' ? (
          <>
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            <span className="text-rose-700 text-sm">Location Off — Location is required to clock in. Please enable location in your browser settings.</span>
          </>
        ) : locationStatus === 'lost' ? (
          <>
            <WifiOff className="w-5 h-5 text-amber-600" />
            <span className="text-amber-700 text-sm">Location signal lost. Please check your GPS. Your clock-in status is not affected.</span>
          </>
        ) : (
          <>
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-blue-700 text-sm">Getting location...</span>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main Clock Section */}
        <div className="lg:col-span-7">
          <Card className="border-0 shadow-xl shadow-stone-200/50 bg-white overflow-hidden">
            <CardContent className="p-8 sm:p-12">
              {/* Current Time Display */}
              <div className="text-center mb-8">
                <p className="text-6xl sm:text-7xl font-bold text-stone-900 font-mono tracking-tight">
                  {fmtTz(currentTime, 'HH:mm')}
                </p>
                <p className="text-stone-400 mt-2 font-mono">{fmtTz(currentTime, 'ss')} seconds</p>
              </div>

              {/* Current Project Display */}
              {isClockedIn && todayEntry?.project_name && (
                <div className="bg-emerald-50 rounded-xl p-4 mb-8 flex items-center justify-center gap-3">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-emerald-800">Working at: {todayEntry.project_name}</span>
                </div>
              )}

              {/* Project Selection (only when not clocked in) */}
              {!isClockedIn && (
                <div className="mb-8 max-w-sm mx-auto">
                  {isDriver && (
                    <p className="flex items-center justify-center gap-2 text-sm text-sky-700 bg-sky-50 rounded-lg py-2 mb-3" data-testid="truck-driver-note">
                      <Truck className="w-4 h-4" /> Truck driver: you can clock in from anywhere
                    </p>
                  )}
                  <Label className="text-stone-600 mb-2 block text-center">{isDriver ? 'Site (optional)' : 'Select Project/Site'}</Label>
                  <Select value={selectedProject} onValueChange={setSelectedProject}>
                    <SelectTrigger data-testid="project-select" className="bg-stone-50 border-stone-200 h-12">
                      <SelectValue placeholder={isDriver ? 'On the road' : "Choose where you're working..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {isDriver && <SelectItem value="none">On the road (no site)</SelectItem>}
                      {projects.map(proj => (
                        <SelectItem key={proj.id} value={proj.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-stone-400" />
                            {proj.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Main Clock Button */}
              <div className="flex justify-center mb-8">
                {!isClockedIn ? (
                  <button
                    onClick={handleClockIn}
                    disabled={actionLoading || (!selectedProject && !isDriver)}
                    data-testid="clock-in-btn"
                    className={`relative w-48 h-48 rounded-full text-white shadow-2xl flex flex-col items-center justify-center gap-2 transition-all border-4 border-emerald-100/20 ${
                      selectedProject || isDriver 
                        ? 'bg-emerald-600 hover:bg-emerald-500 hover:scale-105 active:scale-95 shadow-emerald-600/30' 
                        : 'bg-stone-300 cursor-not-allowed'
                    } disabled:opacity-50 disabled:hover:scale-100`}
                  >
                    {actionLoading ? (
                      <Loader2 className="w-12 h-12 animate-spin" />
                    ) : (
                      <>
                        <LogIn className="w-12 h-12" />
                        <span className="text-xl font-bold">Clock In</span>
                      </>
                    )}
                    {(selectedProject || isDriver) && (
                      <span className="absolute w-48 h-48 rounded-full border-4 border-emerald-400 animate-pulse-ring" />
                    )}
                  </button>
                ) : isClockedOut ? (
                  <div className="w-48 h-48 rounded-full bg-stone-100 text-stone-400 flex flex-col items-center justify-center gap-2 border-4 border-stone-200">
                    <Clock className="w-12 h-12" />
                    <span className="text-xl font-bold">Day Complete</span>
                  </div>
                ) : (
                  <button
                    onClick={handleClockOut}
                    disabled={actionLoading}
                    data-testid="clock-out-btn"
                    className="w-48 h-48 rounded-full bg-rose-500 hover:bg-rose-400 text-white shadow-2xl shadow-rose-500/30 flex flex-col items-center justify-center gap-2 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:hover:scale-100 border-4 border-rose-100/20"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-12 h-12 animate-spin" />
                    ) : (
                      <>
                        <LogOut className="w-12 h-12" />
                        <span className="text-xl font-bold">Clock Out</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Break Button */}
              {isWorking && !isClockedOut && (
                <div className="flex justify-center">
                  <Button
                    onClick={handleBreakStart}
                    disabled={actionLoading}
                    variant="outline"
                    size="lg"
                    data-testid="break-start-btn"
                    className="px-8 py-6 text-lg rounded-xl border-2 border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300 transition-all"
                  >
                    <Coffee className="w-5 h-5 mr-2" />
                    Take a Break
                  </Button>
                </div>
              )}

              {isOnBreak && (
                <div className="flex justify-center">
                  <Button
                    onClick={handleBreakEnd}
                    disabled={actionLoading}
                    size="lg"
                    data-testid="break-end-btn"
                    className="px-8 py-6 text-lg rounded-xl bg-amber-500 hover:bg-amber-400 text-white shadow-lg shadow-amber-500/20 transition-all"
                  >
                    <Coffee className="w-5 h-5 mr-2" />
                    End Break
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stats Section */}
        <div className="lg:col-span-5 space-y-6">
          {/* Today's Summary */}
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Today's Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <p className="text-sm text-emerald-600 font-medium">Work Time</p>
                  <p className="text-2xl font-bold text-emerald-800 font-mono">
                    {formatMinutes(todayEntry?.total_work_minutes || 0)}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4">
                  <p className="text-sm text-amber-600 font-medium">Break Time</p>
                  <p className="text-2xl font-bold text-amber-800 font-mono">
                    {formatMinutes(todayEntry?.total_break_minutes || 0)}
                  </p>
                </div>
              </div>

              <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-stone-500 text-sm">Project</span>
                  <span className="font-medium text-stone-800" data-testid="current-project">
                    {todayEntry?.project_name || '-'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-500 text-sm">Clock In</span>
                  <span className="font-mono font-medium text-stone-800" data-testid="clock-in-time">
                    {formatTime(todayEntry?.clock_in)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-500 text-sm">Clock Out</span>
                  <span className="font-mono font-medium text-stone-800" data-testid="clock-out-time">
                    {formatTime(todayEntry?.clock_out)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-stone-500 text-sm">Breaks Taken</span>
                  <span className="font-mono font-medium text-stone-800">
                    {todayEntry?.breaks?.length || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Link to="/incidents" className="block" data-testid="dashboard-report-incident-link">
            <Card className="border-0 shadow-lg shadow-stone-200/50 bg-white hover:bg-rose-50 transition-colors">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <p className="font-bold text-stone-900">Report an Incident</p>
                  <p className="text-sm text-stone-500">Injury, near miss or damage, with photos</p>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Quick Tips */}
          <Card className="border-0 shadow-lg shadow-stone-200/50 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Timer className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">Pro Tip</h3>
                  <p className="text-emerald-100 text-sm leading-relaxed">
                    {!isClockedIn && isDriver
                      ? "Allow location, then tap Clock In. Pick a site only if you're working at one today."
                      : !isClockedIn
                      ? "Select your project/site first, then clock in to start tracking your hours!"
                      : "Taking regular breaks helps maintain productivity. Try the 52-17 method: work for 52 minutes, then take a 17-minute break!"
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
